import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { LEAD_STATUSES, EVENT_TYPES, CITIES, HEAR_ABOUT, statusTone } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DeleteConfirmDialog } from "@/components/admin/DeleteConfirmDialog";
import { Search, Plus, Phone, Mail, ArrowRightCircle, Trash2, Clock, Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";

const emptyLead = {
  full_name: "", email: "", phone: "", event_date: "", event_city: "", event_type: "",
  guest_count: "", service_type: "", preferred_cocktails: "", how_heard: "", notes: "", status: "New lead",
};

const VIEWS = [["active", "Active"], ["archived", "Archived"], ["all", "All"]];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [view, setView] = useState("active");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [testOnly, setTestOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyLead);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const load = () => api.get(`/leads?view=${view}`).then((r) => { setLeads(r.data); setSelectedIds([]); }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);
  useEffect(() => { if (params.get("new") === "1") { setCreateOpen(true); setParams({}); } }, [params]);

  const isTest = (l) => {
    const hay = `${l.full_name || ""} ${l.email || ""}`.toLowerCase();
    return hay.includes("example.com") || hay.includes("test") || hay.includes("demo");
  };

  const filtered = useMemo(() => leads.filter((l) => {
    const s = search.toLowerCase();
    const match = !s || [l.full_name, l.email, l.phone, l.event_city].some((v) => (v || "").toLowerCase().includes(s));
    const st = !statusFilter || l.status === statusFilter;
    const t = !testOnly || isTest(l);
    const created = (l.created_at || "").slice(0, 10);
    const df = !dateFrom || (created && created >= dateFrom);
    const dt = !dateTo || (created && created <= dateTo);
    return match && st && t && df && dt;
  }), [leads, search, statusFilter, testOnly, dateFrom, dateTo]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((l) => selectedIds.includes(l.id));
  const toggleSelectAll = () => setSelectedIds(allVisibleSelected ? [] : filtered.map((l) => l.id));

  const create = async () => {
    if (!form.full_name || !form.email) { toast.error("Name and email are required"); return; }
    try {
      const payload = { ...form, guest_count: form.guest_count === "" ? null : Number(form.guest_count) };
      await api.post("/leads", payload);
      toast.success("Lead created");
      setCreateOpen(false); setForm(emptyLead); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openLead = async (id) => { const { data } = await api.get(`/leads/${id}`); setSelected(data); };
  const updateLead = async (updates) => { const { data } = await api.patch(`/leads/${selected.id}`, updates); setSelected(data); load(); };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.post(`/leads/${selected.id}/timeline`, { type: "note", text: note });
    setSelected(data); setNote(""); toast.success("Note added");
  };
  const convert = async () => {
    const { data } = await api.post(`/leads/${selected.id}/convert`);
    toast.success("Converted to event");
    setSelected(null); load();
    navigate(`/admin/events/${data.id}`);
  };

  const archiveLead = async (id, archived) => {
    await api.patch(`/leads/${id}/archive`, { archived });
    toast.success(archived ? "Lead archived" : "Lead restored");
    if (selected?.id === id) setSelected(null);
    load();
  };
  const deleteLead = async (id) => {
    await api.delete(`/leads/${id}`);
    toast.success("Lead permanently deleted");
    if (selected?.id === id) setSelected(null);
    load();
  };
  const bulkAction = async (action) => {
    if (!selectedIds.length) return;
    const n = selectedIds.length;
    await api.post("/leads/bulk", { ids: selectedIds, action });
    toast.success(`${action === "delete" ? "Deleted" : action === "archive" ? "Archived" : "Restored"} ${n} lead(s)`);
    load();
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div><p className="eyebrow mb-2">CRM</p><h1 className="font-serif text-4xl">Leads</h1></div>
        <Button onClick={() => setCreateOpen(true)} className="btn-dark" data-testid="new-lead-button"><Plus className="h-4 w-4" /> New Lead</Button>
      </div>

      <div className="flex items-center gap-2 mb-4" data-testid="leads-view-tabs">
        {VIEWS.map(([v, label]) => (
          <button key={v} onClick={() => setView(v)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${view === v ? "bg-ink text-white" : "bg-clay text-muted-foreground hover:text-ink"}`} data-testid={`leads-view-${v}`}>{label}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone, city…" className="df-input pl-10" data-testid="leads-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="df-input max-w-[220px]" data-testid="leads-status-filter">
          <option value="">All statuses</option>
          {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-5">
        <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="leads-test-only"><Checkbox checked={testOnly} onCheckedChange={(v) => setTestOnly(!!v)} /> Test/demo only</label>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Created</span>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="df-input max-w-[160px]" data-testid="leads-date-from" />
          <span className="text-muted-foreground">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="df-input max-w-[160px]" data-testid="leads-date-to" />
        </div>
        {filtered.length > 0 && (
          <label className="flex items-center gap-2 text-sm cursor-pointer ml-auto" data-testid="leads-select-all"><Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAll} /> Select all ({filtered.length})</label>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="df-card p-3 mb-4 flex flex-wrap items-center gap-2 sticky top-2 z-20 bg-cream/95 backdrop-blur" data-testid="leads-bulk-bar">
          <span className="text-sm font-medium mr-1">{selectedIds.length} selected</span>
          {view !== "archived" && <Button onClick={() => bulkAction("archive")} className="btn-outline text-xs" data-testid="leads-bulk-archive"><Archive className="h-3 w-3" /> Archive</Button>}
          {view !== "active" && <Button onClick={() => bulkAction("unarchive")} className="btn-outline text-xs" data-testid="leads-bulk-restore"><ArchiveRestore className="h-3 w-3" /> Restore</Button>}
          <Button onClick={() => setBulkDeleteOpen(true)} className="text-xs bg-destructive text-white hover:bg-destructive/90 h-9 px-3" data-testid="leads-bulk-delete"><Trash2 className="h-3 w-3" /> Delete</Button>
          <button onClick={() => setSelectedIds([])} className="text-xs text-muted-foreground ml-auto">Clear</button>
        </div>
      )}

      <div className="df-card divide-y divide-line overflow-hidden">
        {filtered.length === 0 && <p className="text-sm text-muted-foreground py-16 text-center">No leads found.</p>}
        {filtered.map((l) => (
          <div key={l.id} className={`p-4 sm:p-5 flex items-center gap-4 hover:bg-clay/60 transition-colors ${l.archived ? "opacity-70" : ""}`} data-testid={`lead-row-${l.id}`}>
            <div onClick={(ev) => ev.stopPropagation()}>
              <Checkbox checked={selectedIds.includes(l.id)} onCheckedChange={() => toggleSelect(l.id)} data-testid={`lead-select-${l.id}`} />
            </div>
            <button onClick={() => openLead(l.id)} className="flex-1 min-w-0 text-left flex items-center justify-between gap-4" data-testid={`lead-open-${l.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{l.full_name}{l.archived && <span className="ml-2 text-[9px] uppercase tracking-widest bg-clay text-muted-foreground px-2 py-0.5 rounded-full align-middle">Archived</span>}</div>
                <div className="text-xs text-muted-foreground truncate">{l.email} · {l.event_type || "—"} · {l.event_city || "—"}</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="hidden sm:block text-xs text-muted-foreground">{l.event_date || ""}</span>
                <Badge variant="outline" className={statusTone(l.status)}>{l.status}</Badge>
              </div>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              {l.archived
                ? <button onClick={() => archiveLead(l.id, false)} title="Restore" className="p-2 text-olive" data-testid={`lead-restore-${l.id}`}><ArchiveRestore className="h-4 w-4" /></button>
                : <button onClick={() => archiveLead(l.id, true)} title="Archive" className="p-2 text-muted-foreground hover:text-ink" data-testid={`lead-archive-${l.id}`}><Archive className="h-4 w-4" /></button>}
              <button onClick={() => setDeleteTarget(l)} title="Delete" className="p-2 text-destructive" data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Lead</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <F label="Full name *"><Input className="df-input" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="new-lead-name" /></F>
            <F label="Email *"><Input className="df-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="new-lead-email" /></F>
            <F label="Phone"><Input className="df-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></F>
            <F label="Event date"><Input type="date" className="df-input" value={form.event_date} onChange={(e) => set("event_date", e.target.value)} /></F>
            <F label="Event type"><select className="df-input" value={form.event_type} onChange={(e) => set("event_type", e.target.value)}><option value="">Select…</option>{EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></F>
            <F label="City"><select className="df-input" value={form.event_city} onChange={(e) => set("event_city", e.target.value)}><option value="">Select…</option>{CITIES.map((c) => <option key={c}>{c}</option>)}</select></F>
            <F label="Guest count"><Input type="number" className="df-input" value={form.guest_count} onChange={(e) => set("guest_count", e.target.value)} /></F>
            <F label="Service type"><select className="df-input" value={form.service_type} onChange={(e) => set("service_type", e.target.value)}><option value="">Select…</option><option>Alcohol service</option><option>Mocktail-only service</option></select></F>
            <F label="How heard"><select className="df-input" value={form.how_heard} onChange={(e) => set("how_heard", e.target.value)}><option value="">Select…</option>{HEAR_ABOUT.map((h) => <option key={h}>{h}</option>)}</select></F>
            <F label="Preferred cocktails" full><Textarea className="df-input" value={form.preferred_cocktails} onChange={(e) => set("preferred_cocktails", e.target.value)} /></F>
            <F label="Notes" full><Textarea className="df-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></F>
          </div>
          <Button onClick={create} className="btn-dark w-full mt-2" data-testid="save-lead-button">Create Lead</Button>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl">{selected.full_name}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-2 mb-4">
                {selected.phone && <a href={`tel:${selected.phone}`} className="btn-outline text-xs py-2 px-4" data-testid="lead-call"><Phone className="h-3 w-3" /> Call</a>}
                <a href={`mailto:${selected.email}`} className="btn-outline text-xs py-2 px-4" data-testid="lead-email"><Mail className="h-3 w-3" /> Email</a>
                <Button onClick={convert} className="btn-dark text-xs py-2 px-4 h-auto" data-testid="lead-convert"><ArrowRightCircle className="h-3 w-3" /> Convert to Event</Button>
                {selected.archived
                  ? <Button onClick={() => archiveLead(selected.id, false)} variant="ghost" className="text-xs py-2 px-4 h-auto text-olive hover:bg-olive/10" data-testid="lead-restore"><ArchiveRestore className="h-3 w-3" /> Restore</Button>
                  : <Button onClick={() => archiveLead(selected.id, true)} variant="ghost" className="text-xs py-2 px-4 h-auto" data-testid="lead-archive"><Archive className="h-3 w-3" /> Archive</Button>}
                <Button onClick={() => setDeleteTarget(selected)} variant="ghost" className="text-xs py-2 px-4 h-auto text-destructive hover:bg-destructive/10" data-testid="lead-delete"><Trash2 className="h-3 w-3" /> Delete</Button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 text-sm mb-5">
                <Info label="Email" value={selected.email} />
                <Info label="Phone" value={selected.phone} />
                <Info label="Event date" value={selected.event_date} />
                <Info label="Event type" value={selected.event_type} />
                <Info label="City" value={selected.event_city} />
                <Info label="Guests" value={selected.guest_count} />
                <Info label="Service" value={selected.service_type} />
                <Info label="How heard" value={selected.how_heard} />
              </div>
              {selected.preferred_cocktails && <div className="mb-4 text-sm"><span className="df-label">Preferred cocktails</span>{selected.preferred_cocktails}</div>}

              <div className="grid sm:grid-cols-2 gap-4 mb-5">
                <div>
                  <Label className="df-label">Status</Label>
                  <select className="df-input" value={selected.status} onChange={(e) => updateLead({ status: e.target.value })} data-testid="lead-status-select">
                    {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <Label className="df-label">Internal notes</Label>
                <Textarea className="df-input" defaultValue={selected.internal_notes} onBlur={(e) => updateLead({ internal_notes: e.target.value })} data-testid="lead-internal-notes" />
              </div>

              <div className="border-t border-line pt-4">
                <p className="df-label flex items-center gap-2"><Clock className="h-3 w-3" /> Timeline</p>
                <div className="flex gap-2 mb-4">
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="df-input" data-testid="lead-note-input" />
                  <Button onClick={addNote} className="btn-dark" data-testid="lead-add-note">Add</Button>
                </div>
                <div className="space-y-3">
                  {(selected.timeline || []).slice().reverse().map((t, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <div className="h-2 w-2 rounded-full bg-olive mt-1.5 shrink-0" />
                      <div><span className="text-ink">{t.text}</span><div className="text-xs text-muted-foreground">{new Date(t.ts).toLocaleString()}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteLead(deleteTarget.id)}
        description="Are you sure you want to permanently delete this lead and all related records (CRM timeline, related webhook logs, and any event it was converted into — including that event's quote, contract, invoice, menu, alcohol list and supplies)? This action cannot be undone."
        protectedWarning={deleteTarget && deleteTarget.status === "Converted to event" ? "This lead was converted to an event. Deleting it will also permanently remove that event and all its documents." : ""}
      />

      <DeleteConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedIds.length}
        onConfirm={() => bulkAction("delete")}
        description={`Are you sure you want to permanently delete ${selectedIds.length} lead(s) and all their related records (including any converted events)? This action cannot be undone.`}
      />
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}><Label className="df-label">{label}</Label>{children}</div>
);
const Info = ({ label, value }) => (
  <div><span className="df-label">{label}</span><span>{value || "—"}</span></div>
);
